/*
 * CAF — TeeInBlue in-drawer Edit (mini-cart / slide cart).
 *
 * WHY: TeeInBlue's app-v1.js only auto-runs its cart-edit scan on product and
 * /cart pages (Lighthouse gate, per TeeInBlue docs). The mini-cart drawer opens
 * on ANY page, and its `.tee-edit-customization[data-customization-id]` slots
 * (rendered by sections/header.liquid + assets/mini-cart-drawer.js) stay empty
 * off product/cart pages -> the theme fallback "Edit" showed and routed to /cart.
 *
 * FIX (verified live): seeding `window.teeinblueShopConfig` with the FULL store
 * config BEFORE app-v1.js inits + dispatching `teeinblue-event-page-changed` after
 * the drawer renders makes TeeInBlue inject its real edit/preview buttons into the
 * drawer, and clicking one opens the TeeInBlue editor IN PLACE (`.tee-edit-popup`).
 * We then close the drawer so the popup isn't hidden under the fixed drawer.
 *
 * The FULL config matters for LAYOUT: seeding only `show_edit_button` still injects
 * but the editor renders TeeInBlue's compact/mobile layout instead of the wide
 * gallery+form layout the /cart page shows. So we grab the real, fresh config the
 * app embed already prints inline on /cart (`window.teeinblueShopConfig = {...}`,
 * valid JSON after the spread), fetched once per page and cached, so the drawer
 * editor matches the /cart editor without hardcoding ~200 settings that would drift.
 *
 * Isolated layer: does NOT modify mini-cart-drawer.js, only acts when the drawer
 * holds a customized line with an empty tee slot, and never overwrites a config
 * TeeInBlue already populated (product/cart pages). If anything fails, the drawer
 * keeps its existing route-to-/cart?caf_edit_customization_id fallback.
 *
 * v3 (cart pass, 2026-10-04) — the drawer comes BACK after editing (it used to stay closed):
 *  - editor closed (×, Escape, overlay) → CAFMiniCartDrawer.fetchRefreshAndOpen({ reason: 'edit' });
 *  - "Update cart" → TeeInBlue reloads the page, so a short-lived sessionStorage note
 *    (`caf-mc-reopen`, 2 min) reopens the drawer on the next load with "✓ Updated"; if TeeInBlue
 *    does not navigate, the drawer reopens in place after the update settles.
 *  Read-only on app DOM (polling `.tee-edit-popup` visibility); the editor itself is untouched.
 */
(function () {
  'use strict';

  var DRAWER = '[data-cart-form="mini-cart"]';
  var EDIT_SLOT = DRAWER + ' .tee-edit-customization[data-customization-id]';
  var EDIT_BTN =
    DRAWER + ' .tee-edit-customization button, ' +
    DRAWER + ' .tee-edit-customization a, ' +
    DRAWER + ' .tee-edit-customization [role="button"]';
  var TEE_POPUP = '.tee-edit-popup';

  // Fallback flags if the /cart config fetch fails — enough to inject (compact layout).
  var TEE_CART_CONFIG_FALLBACK = { show_edit_button: true, show_preview_button: true };

  // Pull the real store config the app embed prints inline on /cart. It's written
  // as `window.teeinblueShopConfig = { ...{"<valid JSON>"}, ... }`; we extract the
  // brace-balanced object (JSON-parseable) so the drawer editor matches /cart.
  function extractCartConfig(html) {
    var marker = html.indexOf('teeinblueShopConfig');
    if (marker < 0) return null;
    var spread = html.indexOf('...{', marker);
    var start = html.indexOf('{', spread >= 0 ? spread + 3 : marker);
    if (start < 0) return null;
    var depth = 0, inStr = false, esc = false;
    for (var j = start; j < html.length; j++) {
      var c = html[j];
      if (inStr) {
        if (esc) esc = false;
        else if (c === '\\') esc = true;
        else if (c === '"') inStr = false;
      } else if (c === '"') inStr = true;
      else if (c === '{') depth++;
      else if (c === '}') {
        depth--;
        if (depth === 0) {
          try { return JSON.parse(html.slice(start, j + 1)); } catch (e) { return null; }
        }
      }
    }
    return null;
  }

  var fullConfigPromise = null;
  function fetchFullCartConfig() {
    if (fullConfigPromise) return fullConfigPromise;
    fullConfigPromise = fetch('/cart', { credentials: 'same-origin' })
      .then(function (res) { return res.text(); })
      .then(function (html) { return extractCartConfig(html); })
      .catch(function () { return null; });
    return fullConfigPromise;
  }

  function seedConfig(full) {
    var existing = window.teeinblueShopConfig || {};
    // Never clobber a config TeeInBlue already populated (product/cart pages).
    if (Object.keys(existing).length > 20) return;
    var cfg = full ? Object.assign({}, full) : Object.assign({}, TEE_CART_CONFIG_FALLBACK);
    cfg.show_edit_button = true;
    cfg.show_preview_button = true;
    window.teeinblueShopConfig = cfg;
  }

  function ensureAppV1(onReady) {
    var existing = document.querySelector('script[src*="sdk.teeinblue.com/shopify/app-v1.js"]');
    if (existing) {
      if (window.TEEINBLUE_APP) { onReady(); return; }
      existing.addEventListener('load', onReady, { once: true });
      window.setTimeout(onReady, 3000);
      return;
    }
    var async = document.querySelector('script[src*="sdk.teeinblue.com/async.js"]');
    if (!async || !async.src) { onReady(); return; }
    try {
      var url = new URL(async.src, window.location.origin);
      var token = url.searchParams.get('token');
      var shop = url.searchParams.get('shop');
      if (!token || !shop) { onReady(); return; }
      var script = document.createElement('script');
      script.src = 'https://sdk.teeinblue.com/shopify/app-v1.js?token=' +
        encodeURIComponent(token) + '&shop=' + encodeURIComponent(shop);
      script.defer = true;
      script.addEventListener('load', onReady, { once: true });
      script.addEventListener('error', onReady, { once: true });
      document.head.appendChild(script);
      window.setTimeout(onReady, 4000);
    } catch (error) {
      onReady();
    }
  }

  function hasEmptyEditSlot() {
    var slots = document.querySelectorAll(EDIT_SLOT);
    for (var i = 0; i < slots.length; i++) {
      if (!slots[i].children.length) return true;
    }
    return false;
  }

  function dispatchPageChanged() {
    document.dispatchEvent(new CustomEvent('teeinblue-event-page-changed'));
  }

  var priming = false;

  // The mini-cart drawer only opens on desktop (>=799px, matches
  // mini-cart-drawer.js canOpenMiniCartDrawer). Priming fetches /cart + loads
  // TeeInBlue's app-v1.js, so gate it here to avoid that work on mobile — where
  // the drawer (and its in-place editor) can never open anyway.
  function canDrawerOpen() {
    return window.matchMedia('(min-width: 799px)').matches;
  }

  // Called after each drawer render. Seeds the (full) config, loads app-v1.js if
  // needed, and nudges TeeInBlue to (re-)inject into the freshly rendered slots.
  function primeDrawerEdit() {
    if (priming) return;
    if (!canDrawerOpen()) return; // drawer is desktop-only; skip on mobile
    if (!document.querySelector(EDIT_SLOT)) return; // no customized line in the drawer
    if (!hasEmptyEditSlot()) return; // TeeInBlue already injected

    priming = true;
    fetchFullCartConfig().then(function (full) {
      seedConfig(full);
      ensureAppV1(function () {
        dispatchPageChanged();
        [400, 1000, 2200].forEach(function (delay) {
          window.setTimeout(function () { if (hasEmptyEditSlot()) dispatchPageChanged(); }, delay);
        });
        window.setTimeout(function () { priming = false; }, 2600);
      });
    });
  }

  // TeeInBlue's editor (.tee-edit-popup) opens under the fixed drawer; close the
  // drawer so it's fully visible. Same outcome as being on the /cart page.
  function closeDrawer() {
    if (!document.body || !document.body.classList.contains('caf-mini-cart-open')) return;
    var api = window.CAFMiniCartDrawer;
    if (api && typeof api.close === 'function') { api.close(); return; }
    document.body.classList.remove('caf-mini-cart-open');
    var active = document.querySelectorAll('.cart-container.active_link');
    for (var i = 0; i < active.length; i++) active[i].classList.remove('active_link');
  }

  // .tee-edit-popup is a fixed-position modal, so offsetParent is null even when
  // shown — check size + computed style instead.
  function isVisible(el) {
    if (!el) return false;
    var rect = el.getBoundingClientRect();
    if (rect.width < 100 || rect.height < 100) return false;
    var style = window.getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
  }

  var REOPEN_KEY = 'caf-mc-reopen';
  var REOPEN_TTL = 120000;
  var editSession = null; // { updating: bool } while an editor opened from the drawer is up
  var pageLeaving = false;

  window.addEventListener('pagehide', function () { pageLeaving = true; });
  window.addEventListener('beforeunload', function () { pageLeaving = true; });

  function writeReopenNote(reason) {
    try {
      window.sessionStorage.setItem(REOPEN_KEY, JSON.stringify({ t: Date.now(), reason: reason, path: window.location.pathname }));
    } catch (e) { /* storage unavailable */ }
  }

  function readReopenNote() {
    try {
      var note = JSON.parse(window.sessionStorage.getItem(REOPEN_KEY) || 'null');
      window.sessionStorage.removeItem(REOPEN_KEY);
      return note && Date.now() - Number(note.t || 0) < REOPEN_TTL ? note : null;
    } catch (e) {
      return null;
    }
  }

  function reopenDrawer(reason) {
    var api = window.CAFMiniCartDrawer;
    if (!api || typeof api.fetchRefreshAndOpen !== 'function') return;
    if (typeof api.canOpen === 'function' && !api.canOpen()) return;
    api.fetchRefreshAndOpen({ reason: reason });
  }

  // While the editor (opened from the drawer) is up, wait for it to go away, then bring the drawer back.
  function watchEditorUntilClosed() {
    var timer = window.setInterval(function () {
      if (isVisible(document.querySelector(TEE_POPUP))) return;
      window.clearInterval(timer);
      var session = editSession;
      editSession = null;
      if (!session || pageLeaving) return;
      if (session.updating) {
        // TeeInBlue normally reloads after "Update cart" (the note reopens the drawer on the next
        // load). If it doesn't navigate, reopen here once the update has settled.
        window.setTimeout(function () {
          if (pageLeaving) return;
          readReopenNote();
          reopenDrawer('updated');
        }, 2500);
        return;
      }
      readReopenNote();
      reopenDrawer('edit');
    }, 300);
  }

  function watchForPopupThenCloseDrawer() {
    var tries = 0;
    var timer = window.setInterval(function () {
      tries++;
      if (isVisible(document.querySelector(TEE_POPUP))) {
        closeDrawer();
        window.clearInterval(timer);
        watchEditorUntilClosed();
        return;
      }
      if (tries > 40) {
        window.clearInterval(timer);
        editSession = null;
      }
    }, 100);
  }

  ['caf:mini-cart-refreshed', 'caf:mini-cart-opened'].forEach(function (name) {
    document.addEventListener(name, primeDrawerEdit);
  });

  // When a real (TeeInBlue-injected) edit button in the drawer is clicked, wait for
  // the editor popup and close the drawer. The theme fallback link keeps its own
  // /cart route, so this only affects the in-place path.
  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;

    // "Update cart" inside an editor that was opened from the drawer.
    if (editSession && event.target.closest(TEE_POPUP + ' .tee-btn--atc, ' + TEE_POPUP + ' #teeAtcButton')) {
      editSession.updating = true;
      writeReopenNote('updated');
      return;
    }

    if (!event.target.closest(EDIT_BTN)) return;
    editSession = { updating: false };
    watchForPopupThenCloseDrawer();
  }, true);

  // Next page load after "Update cart" (TeeInBlue reloads the page): reopen the drawer once its API exists.
  (function reopenAfterReload() {
    var note = null;
    try { note = JSON.parse(window.sessionStorage.getItem(REOPEN_KEY) || 'null'); } catch (e) { note = null; }
    if (!note) return;
    var tries = 0;
    var timer = window.setInterval(function () {
      tries++;
      var api = window.CAFMiniCartDrawer;
      if (api && typeof api.fetchRefreshAndOpen === 'function') {
        window.clearInterval(timer);
        var fresh = readReopenNote();
        if (fresh && fresh.reason === 'updated') reopenDrawer('updated');
        return;
      }
      if (tries > 50) window.clearInterval(timer);
    }, 100);
  })();

  // Warm the config fetch early so the first edit click is instant — desktop only
  // (primeDrawerEdit still fetches on demand if the viewport grows past 799px later).
  if (canDrawerOpen() && document.querySelector(EDIT_SLOT)) fetchFullCartConfig();

  // If the drawer is already open at script load (rare), prime once.
  if (document.body && document.body.classList.contains('caf-mini-cart-open')) primeDrawerEdit();
})();
